import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, ChevronRight, X, Rocket } from 'lucide-react';
import {
  uxService, OnboardingStatus, getCachedPreferences, setCachedPreference,
} from '../services/uxService';
import './SetupChecklist.css';

const DISMISS_KEY = 'setup-checklist:dismissed';

interface StepDef {
  key: keyof OnboardingStatus;
  labelKey: string;
  fallback: string;
  path: string;
}

const STEPS: StepDef[] = [
  { key: 'companyProfile', labelKey: 'setup.companyProfile', fallback: 'Fill in your company profile', path: '/settings/company' },
  { key: 'hasTaxes', labelKey: 'setup.taxes', fallback: 'Set up your taxes', path: '/products/taxes' },
  { key: 'hasProducts', labelKey: 'setup.products', fallback: 'Add your first product', path: '/products/create' },
  { key: 'printerConfigured', labelKey: 'setup.printer', fallback: 'Configure bill printing', path: '/settings/printing-devices' },
  { key: 'hasFirstSale', labelKey: 'setup.firstSale', fallback: 'Make your first sale', path: '/sales/sales-invoices/create' },
];

/**
 * First-run setup checklist on the dashboard. Steps auto-complete from live
 * data (no bookkeeping); the card disappears once everything is done, or when
 * the user dismisses it (stored per user, server-side).
 */
const SetupChecklist: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [dismissed, setDismissed] = useState(true); // hidden until proven visible

  useEffect(() => {
    let cancelled = false;
    Promise.all([uxService.getOnboardingStatus(), getCachedPreferences()])
      .then(([statusRes, prefs]) => {
        if (cancelled) return;
        setStatus(statusRes.data.data ?? null);
        setDismissed(prefs[DISMISS_KEY] === 'true');
      })
      .catch(() => { /* non-critical dashboard card — stay hidden on error */ });
    return () => { cancelled = true; };
  }, []);

  if (!status || dismissed) return null;

  const doneCount = STEPS.filter((s) => status[s.key]).length;
  if (doneCount === STEPS.length) return null; // fully set up — nothing to teach

  const dismiss = () => {
    setDismissed(true);
    setCachedPreference(DISMISS_KEY, 'true');
  };

  return (
    <div className="ims-card setup-checklist">
      <div className="setup-checklist-header">
        <span className="setup-checklist-title">
          <Rocket size={16} />
          {t('setup.title') || 'Get your system ready'}
        </span>
        <div className="setup-checklist-meta">
          <span className="setup-checklist-progress-label">
            {doneCount}/{STEPS.length}
          </span>
          <button
            className="setup-checklist-dismiss"
            onClick={dismiss}
            title={t('setup.dismiss') || 'Hide this checklist'}
            aria-label={t('setup.dismiss') || 'Hide this checklist'}
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="setup-checklist-bar">
        <div
          className="setup-checklist-bar-fill"
          style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
        />
      </div>

      <ul className="setup-checklist-steps">
        {STEPS.map((step) => {
          const done = status[step.key];
          return (
            <li key={step.key}>
              <button
                className={`setup-checklist-step ${done ? 'setup-checklist-step--done' : ''}`}
                onClick={() => !done && navigate(step.path)}
                disabled={done}
              >
                {done
                  ? <CheckCircle2 size={17} className="setup-step-icon-done" />
                  : <Circle size={17} className="setup-step-icon-todo" />}
                <span className="setup-step-label">{t(step.labelKey) || step.fallback}</span>
                {!done && <ChevronRight size={15} className="setup-step-go" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SetupChecklist;
